import React, { useState, useEffect } from "react";
import { Modal, Badge, Spinner } from "react-bootstrap";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import {
  FiCheck,
  FiX,
  FiMail,
  FiBookOpen,
  FiAward,
  FiCreditCard,
  FiCalendar,
  FiPackage,
  FiUsers,
  FiKey,
  FiEdit2,
  FiSave,
  FiMapPin,
  FiUser,
  FiAlertCircle,
} from "react-icons/fi";

const StudentDetailsModal = ({
  show,
  onHide,
  student,
  onStudentUpdated,
  onRegeneratePassword,
  regenLoadingId,
}) => {
  const { token } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [formData, setFormData] = useState({
    student_name: "",
    email: "",
    phone: "",
    gender: "Male",
    programme: "",
    school: "",
    faculty: "",
    specialization: "",
    batch: "",
    cgpa: "",
    credits: "",
    attending: "",
    day: "",
    companions: "0",
    address: "",
    city: "",
    state: "",
    district: "",
    pincode: "",
    country: "India",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    account_holder_name: "",
    branch_name: "",
    bank_address: "",
    remark: "",
  });

  // Reset form data when student or show state changes
  useEffect(() => {
    if (student) {
      setFormData({
        student_name: student.student_name || "",
        email: student.email || "",
        phone: student.phone || "",
        gender: student.gender || "Male",
        programme: student.programme || "",
        school: student.school || "",
        faculty: student.faculty || "",
        specialization: student.specialization || "",
        batch: student.batch || "",
        cgpa: student.cgpa !== undefined && student.cgpa !== null ? String(student.cgpa) : "",
        credits: student.credits !== undefined && student.credits !== null ? String(student.credits) : "",
        attending:
          student.attending ||
          (student.is_paid ? "inPerson" : "notRegistered"),
        day: student.day || "",
        companions:
          student.companions !== undefined && student.companions !== null
            ? String(student.companions)
            : "0",
        address: student.address || "",
        city: student.city || "",
        state: student.state || "",
        district: student.district || "",
        pincode: student.pincode ? String(student.pincode) : "",
        country: student.country || "India",
        bank_name: student.bank_name || "",
        account_number: student.account_number || "",
        ifsc_code: student.ifsc_code || "",
        account_holder_name: student.account_holder_name || "",
        branch_name: student.branch_name || "",
        bank_address: student.bank_address || "",
        remark: student.remark || "",
      });
      setIsEditing(false);
      setErrorMsg("");
    }
  }, [student, show]);

  if (!student) return null;

  // Extract initials for avatar
  const getInitials = (name) => {
    if (!name) return "ST";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const hasBankInfo =
    student.bank_name ||
    student.account_number ||
    student.ifsc_code ||
    student.account_holder_name;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formData.student_name || !formData.student_name.trim()) {
      setErrorMsg("Student Name is required");
      return;
    }
    if (!formData.email || !formData.email.trim() || !formData.email.includes("@")) {
      setErrorMsg("A valid Email address is required");
      return;
    }

    try {
      setSaving(true);
      setErrorMsg("");
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE}/student/update-student/${student._id || student.reg_no}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updated = res.data?.data || { ...student, ...formData };
      if (onStudentUpdated) {
        onStudentUpdated(updated);
      }
      setIsEditing(false);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to update student details. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      {/* Header with Avatar & Student Info */}
      <Modal.Header
        closeButton
        style={{
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          padding: "18px 24px",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "14px", flex: 1 }}>
          {/* Avatar circle */}
          <div
            style={{
              width: "46px",
              height: "46px",
              borderRadius: "12px",
              background: isEditing
                ? "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"
                : "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "17px",
              fontWeight: 700,
              boxShadow: isEditing
                ? "0 4px 10px rgba(37, 99, 235, 0.25)"
                : "0 4px 10px rgba(234, 88, 12, 0.25)",
              flexShrink: 0,
              transition: "all 0.2s ease",
            }}
          >
            {getInitials(isEditing ? formData.student_name : student.student_name)}
          </div>

          {/* Name & Reg No */}
          <div>
            <div
              style={{
                fontSize: "17px",
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <span>{isEditing ? formData.student_name || "Edit Graduate" : student.student_name}</span>
              <span
                style={{
                  fontFamily: "monospace",
                  fontSize: "12px",
                  fontWeight: 600,
                  backgroundColor: "#f1f5f9",
                  color: "#475569",
                  padding: "2px 8px",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                }}
              >
                {student.reg_no}
              </span>
              {isEditing && (
                <Badge
                  bg="primary"
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    padding: "3px 8px",
                    borderRadius: "6px",
                  }}
                >
                  Editing Mode
                </Badge>
              )}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#64748b",
                marginTop: "3px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexWrap: "wrap",
              }}
            >
              <span>{student.programme}</span>
              <span>•</span>
              <span>{student.batch} Batch</span>
              {student.has_due ? (
                <>
                  <span>•</span>
                  <Badge
                    bg="danger"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "2px 6px",
                      fontSize: "11px",
                    }}
                  >
                    <FiX /> Pending Due (₹{student.total_due_amount || 0})
                  </Badge>
                </>
              ) : (
                <>
                  <span>•</span>
                  <Badge
                    bg="success"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "2px 6px",
                      fontSize: "11px",
                    }}
                  >
                    <FiCheck /> No Dues
                  </Badge>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal.Header>

      {/* Modal Body */}
      <Modal.Body
        style={{
          maxHeight: "75vh",
          overflowY: "auto",
          padding: "20px",
          backgroundColor: "#f8fafc",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {/* Error Alert */}
        {errorMsg && (
          <div
            style={{
              padding: "10px 14px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              borderRadius: "8px",
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FiAlertCircle size={16} style={{ flexShrink: 0 }} />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* ========================================================================= */}
        {/* EDIT MODE FORM */}
        {/* ========================================================================= */}
        {isEditing ? (
          <form onSubmit={handleSaveSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* 1. Basic & Contact Information */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "10px",
                padding: "16px 18px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "13.5px",
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  borderBottom: "1px solid #f1f5f9",
                  paddingBottom: "8px",
                }}
              >
                <FiUser color="#ea580c" size={15} /> Basic & Contact Information
              </div>

              <div className="admin-form-two-col" style={{ gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Student Full Name <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.student_name}
                    onChange={(e) => handleInputChange("student_name", e.target.value)}
                    placeholder="Enter student full name"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Student Email ID <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="e.g. name.reg_no@muj.manipal.edu"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                    required
                  />
                </div>
              </div>

              <div className="admin-form-two-col" style={{ gap: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="10-digit mobile number"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Gender
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleInputChange("gender", e.target.value)}
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      backgroundColor: "#fff",
                      outline: "none",
                      cursor: "pointer",
                    }}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 2. Academic Information */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "10px",
                padding: "16px 18px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "13.5px",
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  borderBottom: "1px solid #f1f5f9",
                  paddingBottom: "8px",
                }}
              >
                <FiBookOpen color="#ea580c" size={15} /> Academic Information
              </div>

              <div className="admin-form-two-col" style={{ gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Programme
                  </label>
                  <input
                    type="text"
                    value={formData.programme}
                    onChange={(e) => handleInputChange("programme", e.target.value)}
                    placeholder="e.g. B.Tech. Computer Science and Engineering"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    School
                  </label>
                  <input
                    type="text"
                    value={formData.school}
                    onChange={(e) => handleInputChange("school", e.target.value)}
                    placeholder="e.g. School of Computer Science and Engineering"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>
              </div>

              <div className="admin-form-two-col" style={{ gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Faculty
                  </label>
                  <input
                    type="text"
                    value={formData.faculty}
                    onChange={(e) => handleInputChange("faculty", e.target.value)}
                    placeholder="e.g. Faculty of Engineering"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Specialization
                  </label>
                  <input
                    type="text"
                    value={formData.specialization}
                    onChange={(e) => handleInputChange("specialization", e.target.value)}
                    placeholder="e.g. Computer Science & Engineering"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Batch
                  </label>
                  <input
                    type="text"
                    value={formData.batch}
                    onChange={(e) => handleInputChange("batch", e.target.value)}
                    placeholder="e.g. 2020-2024"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    CGPA
                  </label>
                  <input
                    type="text"
                    value={formData.cgpa}
                    onChange={(e) => handleInputChange("cgpa", e.target.value)}
                    placeholder="e.g. 8.50"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Total Credits
                  </label>
                  <input
                    type="number"
                    value={formData.credits}
                    onChange={(e) => handleInputChange("credits", e.target.value)}
                    placeholder="e.g. 160"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* 3. Convocation & Registration Settings */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "10px",
                padding: "16px 18px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "13.5px",
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  borderBottom: "1px solid #f1f5f9",
                  paddingBottom: "8px",
                }}
              >
                <FiAward color="#ea580c" size={15} /> Convocation & Registration Configuration
              </div>

              <div className="admin-form-two-col" style={{ gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Attendance Mode
                  </label>
                  <select
                    value={formData.attending || "notRegistered"}
                    onChange={(e) => handleInputChange("attending", e.target.value)}
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      backgroundColor: "#fff",
                      outline: "none",
                      cursor: "pointer",
                    }}
                  >
                    <option value="notRegistered">Not Registered / Pending</option>
                    <option value="inPerson">In-Person Attendance</option>
                    <option value="courier">Degree via Courier (In-Absentia)</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Convocation Slot / Day
                  </label>
                  <input
                    type="text"
                    value={formData.day}
                    onChange={(e) => handleInputChange("day", e.target.value)}
                    placeholder="e.g. 31-10-2026 or 01-11-2026"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                  Companions Accompanying
                </label>
                <select
                  value={formData.companions}
                  onChange={(e) => handleInputChange("companions", e.target.value)}
                  style={{
                    width: "100%",
                    height: "36px",
                    padding: "0 10px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    backgroundColor: "#fff",
                    outline: "none",
                    cursor: "pointer",
                  }}
                >
                  <option value="0">0 Companions</option>
                  <option value="1">1 Companion</option>
                  <option value="2">2 Companions (Maximum)</option>
                </select>
              </div>
            </div>

            {/* 4. Address & Banking Details */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "10px",
                padding: "16px 18px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "13.5px",
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  borderBottom: "1px solid #f1f5f9",
                  paddingBottom: "8px",
                }}
              >
                <FiMapPin color="#ea580c" size={15} /> Mailing Address & Banking Information
              </div>

              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                  Mailing / Courier Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  placeholder="Street address, House No, Flat, etc."
                  style={{
                    width: "100%",
                    height: "36px",
                    padding: "0 10px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    outline: "none",
                  }}
                />
              </div>

              <div className="admin-form-two-col" style={{ gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    placeholder="City"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    State / Province
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange("state", e.target.value)}
                    placeholder="State"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>
              </div>

              <div className="admin-form-two-col" style={{ gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    PIN / Postal Code
                  </label>
                  <input
                    type="text"
                    value={formData.pincode}
                    onChange={(e) => handleInputChange("pincode", e.target.value)}
                    placeholder="6-digit PIN code"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={formData.bank_name}
                    onChange={(e) => handleInputChange("bank_name", e.target.value)}
                    placeholder="e.g. HDFC Bank / ICICI Bank"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>
              </div>

              <div className="admin-form-two-col" style={{ gap: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    Account Number
                  </label>
                  <input
                    type="text"
                    value={formData.account_number}
                    onChange={(e) => handleInputChange("account_number", e.target.value)}
                    placeholder="Bank Account Number"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "4px" }}>
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    value={formData.ifsc_code}
                    onChange={(e) => handleInputChange("ifsc_code", e.target.value)}
                    placeholder="e.g. HDFC0001234"
                    style={{
                      width: "100%",
                      height: "36px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  />
                </div>
              </div>
            </div>
          </form>
        ) : (
          /* ========================================================================= */
          /* VIEW MODE (READ ONLY DETAILS) */
          /* ========================================================================= */
          <>
            {/* Section 1: Academic Information */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                padding: "18px 20px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderBottom: "1px solid #f1f5f9",
                  paddingBottom: "8px",
                }}
              >
                <FiBookOpen color="#ea580c" size={16} /> Academic Information
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "14px",
                }}
              >
                {/* Faculty */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Faculty
                  </div>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                    {student.faculty || "N/A"}
                  </div>
                </div>

                {/* School */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    School
                  </div>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                    {student.school || "N/A"}
                  </div>
                </div>

                {/* Programme */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Programme
                  </div>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                    {student.programme || "N/A"}
                  </div>
                </div>

                {/* Specialization */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Specialization
                  </div>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                    {student.specialization || "General"}
                  </div>
                </div>

                {/* CGPA */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    CGPA
                  </div>
                  <div style={{ marginTop: "3px" }}>
                    <span
                      style={{
                        backgroundColor: "#ecfdf5",
                        color: "#065f46",
                        fontWeight: 700,
                        fontSize: "12.5px",
                        padding: "3px 8px",
                        borderRadius: "6px",
                        border: "1px solid #a7f3d0",
                      }}
                    >
                      {student.cgpa || "N/A"}
                    </span>
                  </div>
                </div>

                {/* Credits */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Total Credits
                  </div>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                    {student.credits || "N/A"}
                  </div>
                </div>

                {/* Gender */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Gender
                  </div>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                    {student.gender || "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Convocation & Registration */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                padding: "18px 20px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderBottom: "1px solid #f1f5f9",
                  paddingBottom: "8px",
                }}
              >
                <FiAward color="#ea580c" size={16} /> Convocation & Registration
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "14px",
                }}
              >
                {/* Attendance Mode */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Attendance Mode
                  </div>
                  <div style={{ marginTop: "4px" }}>
                    {student.attending === "inPerson" ? (
                      <span
                        style={{
                          backgroundColor: "#ecfdf5",
                          color: "#047857",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "12.5px",
                          fontWeight: 600,
                          border: "1px solid #a7f3d0",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <FiCalendar size={13} /> In Person ({student.day || "Date N/A"})
                      </span>
                    ) : student.attending === "courier" ? (
                      <span
                        style={{
                          backgroundColor: "#eff6ff",
                          color: "#1d4ed8",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "12.5px",
                          fontWeight: 600,
                          border: "1px solid #bfdbfe",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <FiPackage size={13} /> Degree via Courier
                      </span>
                    ) : (
                      <span
                        style={{
                          backgroundColor: "#f1f5f9",
                          color: "#64748b",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "12.5px",
                          fontWeight: 600,
                          border: "1px solid #cbd5e1",
                        }}
                      >
                        Not Registered
                      </span>
                    )}
                  </div>
                </div>

                {/* Convocation Slot / Day */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Convocation Slot / Day
                  </div>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                    {student.day || "Not Assigned"}
                  </div>
                </div>

                {/* Companions */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Companions Accompanying
                  </div>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px", display: "flex", alignItems: "center", gap: "5px" }}>
                    <FiUsers size={14} color="#64748b" /> {student.companions || 0} Companion(s)
                  </div>
                </div>

                {/* Dues Status */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Dues & Clearance
                  </div>
                  <div style={{ marginTop: "4px" }}>
                    {student.has_due ? (
                      <span
                        style={{
                          backgroundColor: "#fef2f2",
                          color: "#dc2626",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: 600,
                          border: "1px solid #fecaca",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <FiX size={13} /> Pending Due (₹{student.total_due_amount})
                      </span>
                    ) : (
                      <span
                        style={{
                          backgroundColor: "#ecfdf5",
                          color: "#047857",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: 600,
                          border: "1px solid #a7f3d0",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <FiCheck size={13} /> Cleared (No Dues)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Contact & Communication */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                padding: "18px 20px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#0f172a",
                  marginBottom: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderBottom: "1px solid #f1f5f9",
                  paddingBottom: "8px",
                }}
              >
                <FiMail color="#ea580c" size={16} /> Contact & Communication Information
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "14px",
                }}
              >
                {/* Official Email */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Student Email Address
                  </div>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px", wordBreak: "break-all" }}>
                    {student.email || "N/A"}
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Contact Phone Number
                  </div>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                    {student.phone || "Not Provided"}
                  </div>
                </div>

                {/* Address */}
                {student.address && (
                  <div style={{ gridColumn: "span 2" }}>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Mailing / Courier Address
                    </div>
                    <div style={{ fontSize: "13.5px", color: "#334155", marginTop: "2px" }}>
                      {student.address}
                      {student.city ? `, ${student.city}` : ""}
                      {student.state ? `, ${student.state}` : ""}
                      {student.pincode ? ` - ${student.pincode}` : ""}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section 4: Bank Information (if available) */}
            {hasBankInfo && (
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  padding: "18px 20px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "#0f172a",
                    marginBottom: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderBottom: "1px solid #f1f5f9",
                    paddingBottom: "8px",
                  }}
                >
                  <FiCreditCard color="#ea580c" size={16} /> Banking Information
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "14px",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Bank Name
                    </div>
                    <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                      {student.bank_name || "N/A"}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Account Number
                    </div>
                    <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                      {student.account_number || "N/A"}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      IFSC Code
                    </div>
                    <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                      {student.ifsc_code || "N/A"}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Account Holder Name
                    </div>
                    <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
                      {student.account_holder_name || "N/A"}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      {/* Footer Actions */}
      <Modal.Footer
        style={{
          borderTop: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          padding: "14px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: "12.5px", color: "#64748b" }}>
          {isEditing ? (
            <span style={{ color: "#2563eb", fontWeight: 600 }}>Editing Student • {student.reg_no}</span>
          ) : (
            <>Registration Record • <strong>{student.reg_no}</strong></>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          {isEditing ? (
            <>
              {/* Cancel Button */}
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setErrorMsg("");
                }}
                disabled={saving}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#f1f5f9",
                  color: "#475569",
                  fontWeight: 600,
                  fontSize: "13px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                Cancel
              </button>

              {/* Save Changes Button */}
              <button
                type="button"
                onClick={handleSaveSubmit}
                disabled={saving}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 20px",
                  backgroundColor: "#2563eb",
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "13px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: saving ? "not-allowed" : "pointer",
                  boxShadow: "0 2px 6px rgba(37, 99, 235, 0.3)",
                  transition: "all 0.15s ease",
                }}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" style={{ width: "13px", height: "13px", borderWidth: "2px" }} />
                    <span>Saving Changes...</span>
                  </>
                ) : (
                  <>
                    <FiSave size={14} />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              {/* Edit Student Details Button */}
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                title="Edit student details, email, contact, and academic info"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 16px",
                  backgroundColor: "#eff6ff",
                  color: "#1d4ed8",
                  fontWeight: 600,
                  fontSize: "13px",
                  borderRadius: "8px",
                  border: "1px solid #bfdbfe",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#2563eb";
                  e.currentTarget.style.color = "#ffffff";
                  e.currentTarget.style.borderColor = "#2563eb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#eff6ff";
                  e.currentTarget.style.color = "#1d4ed8";
                  e.currentTarget.style.borderColor = "#bfdbfe";
                }}
              >
                <FiEdit2 size={14} />
                <span>Edit Details</span>
              </button>

              {/* Regenerate Password Button */}
              {(student.has_password || student.password) && (
                <button
                  type="button"
                  onClick={() => onRegeneratePassword && onRegeneratePassword(student)}
                  disabled={regenLoadingId === (student._id || student.reg_no)}
                  title="Generate a new secure password and email it directly to student"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    backgroundColor: "#fff7ed",
                    color: "#c2410c",
                    fontWeight: 600,
                    fontSize: "13px",
                    borderRadius: "8px",
                    border: "1px solid #fed7aa",
                    cursor:
                      regenLoadingId === (student._id || student.reg_no)
                        ? "not-allowed"
                        : "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (regenLoadingId !== (student._id || student.reg_no)) {
                      e.currentTarget.style.backgroundColor = "#ea580c";
                      e.currentTarget.style.color = "#ffffff";
                      e.currentTarget.style.borderColor = "#ea580c";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (regenLoadingId !== (student._id || student.reg_no)) {
                      e.currentTarget.style.backgroundColor = "#fff7ed";
                      e.currentTarget.style.color = "#c2410c";
                      e.currentTarget.style.borderColor = "#fed7aa";
                    }
                  }}
                >
                  {regenLoadingId === (student._id || student.reg_no) ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        style={{ width: "13px", height: "13px", borderWidth: "2px" }}
                      />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <FiKey size={14} />
                      <span>Resend Password</span>
                    </>
                  )}
                </button>
              )}

              {/* Close Modal */}
              <button
                type="button"
                onClick={onHide}
                style={{
                  padding: "8px 18px",
                  backgroundColor: "#f1f5f9",
                  color: "#334155",
                  fontWeight: 600,
                  fontSize: "13px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#e2e8f0";
                  e.currentTarget.style.color = "#0f172a";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f1f5f9";
                  e.currentTarget.style.color = "#334155";
                }}
              >
                Close
              </button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default StudentDetailsModal;
